import React, { type FC, useCallback, useEffect, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import {
  PageTitle,
  ReactTableComponent,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import api from "../../../../util/api";
import {
  DashboardSubscriptionItem,
  SubscriptionsDashboardData,
} from "../../../../util/types";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../constants/routes";
import { DateTime } from "luxon";

// ─────────────────────────────────────────────────────────────────────────────
// Styled
// ─────────────────────────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ForecastGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div<{ $accent: string }>`
  background: linear-gradient(135deg, #fff 0%, #f7fafc 100%);
  border: 1px solid #dbe3eb;
  border-left: 6px solid ${(p) => p.$accent};
  border-radius: 12px;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 2px 6px rgba(19, 39, 63, 0.04);
`;

const HeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: #8181a5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const HeroValue = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 34px;
  font-weight: 700;
  color: #13273f;
  line-height: 1.1;
`;

const HeroSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #6c6c8a;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div<{
  $accent?: string;
  $clickable?: boolean;
  $active?: boolean;
}>`
  background: ${(p) => (p.$active ? "#EEF6FF" : "#fff")};
  border: 1px solid ${(p) => (p.$active ? p.$accent || "#027AFF" : "#eee")};
  border-radius: 10px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${(p) => p.$accent && `border-left: 4px solid ${p.$accent};`}
  ${(p) =>
    p.$clickable &&
    `cursor: pointer; transition: box-shadow 0.15s, border-color 0.15s;
     &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); border-color: ${
       p.$accent || "#027AFF"
     }; }`}
`;

const SummaryLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: #8181a5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryValue = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #13273f;
`;

const SummarySubtext = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: #8181a5;
  font-weight: 500;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 30px 20px;
  @media (max-width: 900px) {
    padding: 12px;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 10px;
  gap: 12px;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TableHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1 1 auto;
  min-width: 0;
`;

const SearchInput = styled.input`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  padding: 8px 14px;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
  width: 260px;
  &:focus {
    border-color: #027aff;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const FilterPill = styled.button<{ $active?: boolean; $color?: string }>`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$active ? p.$color || "#027AFF" : "#dbe3eb")};
  background: ${(p) => (p.$active ? (p.$color || "#027AFF") + "14" : "#fff")};
  color: ${(p) => (p.$active ? p.$color || "#027AFF" : "#495b6c")};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover {
    border-color: ${(p) => p.$color || "#027AFF"};
    background: ${(p) => (p.$color || "#027AFF") + "0a"};
  }
`;

const Cell = styled.div`
  padding: 10px 6px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
`;

const SubTag = styled.span<{
  $variant?: "active" | "trial" | "failed" | "warning" | "default";
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $variant }) =>
    $variant === "active"
      ? "rgba(44, 209, 158, 0.17)"
      : $variant === "trial"
      ? "rgba(249, 160, 0, 0.17)"
      : $variant === "failed"
      ? "rgba(239, 99, 85, 0.17)"
      : $variant === "warning"
      ? "rgba(249, 160, 0, 0.17)"
      : "#F4F5F9"};
  color: ${({ $variant }) =>
    $variant === "active"
      ? "#00bc82"
      : $variant === "trial"
      ? "#f9a000"
      : $variant === "failed"
      ? "#ef6355"
      : $variant === "warning"
      ? "#f9a000"
      : "#495b6c"};
`;

const TypeTag = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background: ${(p) => p.$bg};
`;

const AlignmentWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const AlignmentDot = styled.span<{ $ok: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$ok ? "#00bc82" : "#f9a000")};
  flex-shrink: 0;
  margin-top: 4px;
`;

const IssueText = styled.span`
  font-size: 11px;
  color: #f9a000;
  display: block;
  line-height: 1.3;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #8181a5;
`;

const DateCell = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #13273f;
  line-height: 1.3;
`;

const DateSub = styled.span<{ $urgent?: boolean }>`
  font-size: 11px;
  color: ${(p) => (p.$urgent ? "#ef6355" : "#8181a5")};
  font-weight: ${(p) => (p.$urgent ? 600 : 400)};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatPence = (pence: number) =>
  `£${(pence / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getStatusVariant = (
  status: string
): {
  variant: "active" | "trial" | "failed" | "warning" | "default";
  label: string;
} => {
  switch (status) {
    case "active":
      return { variant: "active", label: "Active" };
    case "trialing":
      return { variant: "trial", label: "Trial" };
    case "payment_failed":
      return { variant: "failed", label: "Payment Failed" };
    case "payment_action_required":
      return { variant: "warning", label: "Action Required" };
    case "past_due":
      return { variant: "warning", label: "Past Due" };
    case "incomplete":
      return { variant: "default", label: "Incomplete" };
    case "paused":
      return { variant: "default", label: "Paused" };
    default:
      return { variant: "default", label: status };
  }
};

const getTypeColor = (option: string | null) => {
  switch (option) {
    case "PAYMENTS_ONLY":
      return "#13273F";
    case "TOOLKIT":
      return "#027AFF";
    case "TOOLKIT_MOBILE":
      return "#2CD19E";
    default:
      return "#8181a5";
  }
};

const getTypeLabel = (option: string | null) => {
  switch (option) {
    case "PAYMENTS_ONLY":
      return "Payments Only";
    case "TOOLKIT":
      return "Toolkit";
    case "TOOLKIT_MOBILE":
      return "Mobile";
    default:
      return option ?? "—";
  }
};

const formatUpcomingDate = (
  dateStr: string | null
): { formatted: string; sub: string; urgent: boolean } => {
  if (!dateStr) return { formatted: "—", sub: "", urgent: false };
  const dt = DateTime.fromISO(dateStr);
  if (!dt.isValid) return { formatted: "—", sub: "", urgent: false };
  const now = DateTime.now();
  const diffDays = Math.ceil(dt.diff(now, "days").days);
  const formatted = dt.toFormat("dd MMM yyyy");
  if (diffDays < 0)
    return { formatted, sub: `${Math.abs(diffDays)}d overdue`, urgent: true };
  if (diffDays === 0) return { formatted, sub: "today", urgent: true };
  if (diffDays === 1) return { formatted, sub: "tomorrow", urgent: true };
  if (diffDays <= 3)
    return { formatted, sub: `in ${diffDays} days`, urgent: true };
  if (diffDays <= 7)
    return { formatted, sub: `in ${diffDays} days`, urgent: false };
  return { formatted, sub: `in ${diffDays} days`, urgent: false };
};

type QuickFilter = "all" | "alignment_issues" | "payment_failed";

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const SubscriptionsDashboard: FC = () => {
  const [data, setData] = useState<SubscriptionsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState<QuickFilter>("all");
  const navigate = useNavigate();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filterParam = activeFilter === "all" ? undefined : activeFilter;
      const result = await api.getSubscriptionsDashboard(
        page,
        limit,
        searchDebounced || undefined,
        filterParam
      );
      setData(result);
    } catch (e) {
      console.error("Failed to load dashboard:", e);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchDebounced, activeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [searchDebounced, activeFilter]);

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
  };

  const handleRowClick = (row: DashboardSubscriptionItem) => {
    navigate(AppRoutes.Private.Companies.DETAILS + row.companyId);
  };

  const handleFilterClick = (f: QuickFilter) => {
    setActiveFilter((prev) => (prev === f ? "all" : f));
  };

  const columns = React.useMemo<
    Array<ColumnDef<DashboardSubscriptionItem, any>>
  >(
    () => [
      {
        header: "Company",
        accessorKey: "companyName",
        cell: ({ getValue }) => (
          <Cell>
            <strong>{getValue() ?? "—"}</strong>
          </Cell>
        ),
      },
      {
        header: "Type",
        accessorKey: "subscriptionOption",
        cell: ({ getValue }) => (
          <Cell>
            <TypeTag $bg={getTypeColor(getValue())}>
              {getTypeLabel(getValue())}
            </TypeTag>
          </Cell>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const { variant, label } = getStatusVariant(getValue());
          return (
            <Cell>
              <SubTag $variant={variant}>{label}</SubTag>
            </Cell>
          );
        },
      },
      {
        header: "Provider",
        accessorKey: "preferredBillingProvider",
        cell: ({ row: { original } }: any) => (
          <Cell>
            <SubTag
              $variant={
                (original as any).directDebitActive ? "active" : "default"
              }
            >
              {(original as any).preferredBillingProvider === "gocardless"
                ? "DD"
                : (original as any).preferredBillingProvider === "ryft"
                ? "Card"
                : (original as any).preferredBillingProvider === "manual"
                ? "Manual"
                : (original as any).directDebitActive
                ? "DD"
                : "—"}
            </SubTag>
          </Cell>
        ),
      },
      {
        header: "Next Charge",
        accessorKey: "upcomingCharge",
        cell: ({ row: { original } }: any) => {
          const uc = (original as any).upcomingCharge;
          if (!uc) return <Cell>—</Cell>;
          const { sub, urgent } = formatUpcomingDate(uc.chargeDate);
          return (
            <Cell>
              <DateCell>
                {uc.estimatedAmountPence != null
                  ? formatPence(uc.estimatedAmountPence)
                  : "—"}
                <br />
                <DateSub $urgent={urgent}>
                  {sub || `in ${uc.daysUntilCharge}d`}
                </DateSub>
              </DateCell>
            </Cell>
          );
        },
      },
      {
        header: "Volume",
        accessorKey: "levelUsed",
        cell: ({ getValue }) => (
          // `levelUsed` is in PENCE (Fena billing convention) — use the
          // local `formatPence` helper which divides by 100 and applies
          // locale-grouped thousand separators.
          <Cell>{formatPence((getValue() as number) ?? 0)}</Cell>
        ),
      },
      {
        header: "Txns",
        accessorKey: "unitCount",
        cell: ({ getValue }) => <Cell>{getValue() ?? 0}</Cell>,
      },
      {
        header: "Forecast",
        accessorKey: "upcomingCharge.estimatedAmountPence",
        cell: ({ row: { original } }: any) => {
          const uc = (original as any).upcomingCharge;
          const pence = uc?.estimatedAmountPence;
          if (pence == null) return <Cell>—</Cell>;
          return (
            <Cell>
              <strong style={{ color: "#13273F" }}>{formatPence(pence)}</strong>
              {uc?.type === "tier_based" && uc?.matchedUpToPounds != null && (
                <DateSub
                  style={{ display: "block", color: "#8181a5", fontSize: 11 }}
                >
                  up to £{uc.matchedUpToPounds.toLocaleString("en-GB")} tier
                </DateSub>
              )}
            </Cell>
          );
        },
      },
      {
        header: "Last Month",
        accessorKey: "lastMonth.earningsPence",
        cell: ({ row: { original } }: any) => {
          const lm = (original as any).lastMonth;
          const pence = lm?.earningsPence ?? 0;
          if (pence <= 0) return <Cell style={{ color: "#8181a5" }}>—</Cell>;
          return (
            <Cell>
              <strong>{formatPence(pence)}</strong>
              <DateSub
                style={{ display: "block", color: "#8181a5", fontSize: 11 }}
              >
                {lm.invoiceCount ?? 0} invoice{lm.invoiceCount === 1 ? "" : "s"}
              </DateSub>
            </Cell>
          );
        },
      },
      {
        header: "Alignment",
        accessorKey: "aligned",
        cell: ({ row: { original } }) => (
          <Cell>
            <AlignmentWrap>
              <AlignmentDot $ok={original.aligned} />
              {original.aligned ? (
                <span style={{ fontSize: 12, color: "#00bc82" }}>OK</span>
              ) : (
                <div>
                  {original.issues.map((issue, i) => (
                    <IssueText key={i}>{issue}</IssueText>
                  ))}
                </div>
              )}
            </AlignmentWrap>
          </Cell>
        ),
      },
    ],
    []
  );

  const invoiceCountLabel =
    data && data.invoiceCountThisMonth < 0
      ? `${Math.abs(data.invoiceCountThisMonth)}+ invoices paid`
      : `${data?.invoiceCountThisMonth ?? 0} invoices paid`;

  return (
    <AnimateAppearanceWrapper>
      <Container>
        <TopLine>
          <PageTitle title="Subscriptions Dashboard" />
        </TopLine>

        {/* Business forecast — CEO-level metrics */}
        {data && (
          <ForecastGrid>
            <HeroCard $accent="#13273F">
              <HeroLabel>Forecasted this month</HeroLabel>
              <HeroValue>
                {formatPence(
                  (data.forecastThisMonth as any)?.totalForecastPence ?? 0
                )}
              </HeroValue>
              <HeroSub>
                {data.earningsThisMonth > 0 && (
                  <>
                    {formatPence(data.earningsThisMonth)} already collected ·{" "}
                  </>
                )}
                {(data.forecastThisMonth as any)?.activeSubCount ?? 0} active
                subscriptions
              </HeroSub>
            </HeroCard>

            <HeroCard $accent="#2CD19E">
              <HeroLabel>Last month actuals</HeroLabel>
              <HeroValue>
                {formatPence((data as any).earningsLastMonth ?? 0)}
              </HeroValue>
              <HeroSub>
                {(data as any).invoiceCountLastMonth ?? 0} invoices ·{" "}
                {(() => {
                  const mom = (data as any).monthOverMonthChangePct;
                  if (mom == null) return "No comparison available";
                  const sign = mom > 0 ? "+" : "";
                  const colour =
                    mom > 0 ? "#00BC82" : mom < 0 ? "#EF6355" : "#8181A5";
                  return (
                    <span style={{ color: colour, fontWeight: 600 }}>
                      {sign}
                      {mom.toFixed(1)}% MoM
                    </span>
                  );
                })()}
              </HeroSub>
            </HeroCard>
          </ForecastGrid>
        )}

        {/* Summary Cards — clickable as filters */}
        {data && (
          <SummaryGrid>
            <SummaryCard $accent="#027AFF">
              <SummaryLabel>Earnings this month</SummaryLabel>
              <SummaryValue>{formatPence(data.earningsThisMonth)}</SummaryValue>
              <SummarySubtext>{invoiceCountLabel}</SummarySubtext>
            </SummaryCard>

            <SummaryCard $accent="#027AFF">
              <SummaryLabel>Upcoming Charges</SummaryLabel>
              <SummaryValue>
                {data.upcomingChargesSummary?.totalEstimatedPence
                  ? formatPence(data.upcomingChargesSummary.totalEstimatedPence)
                  : "—"}
              </SummaryValue>
              <SummarySubtext>
                {/*
                  Copy aligned with the backend's new fleet-wide
                  summary: the count is "subs the cron will bill at
                  its next tick", not a 7-day sliding window. The
                  date is the actual cron run time (1st of every
                  month, 16:00 BST). Old "within 7 days" copy was
                  stale and led QA to think £40k was being charged
                  imminently when actually it bills on 1 Jun.
                */}
                {data.upcomingChargesSummary?.companiesWithUpcomingCharge ?? 0}{" "}
                companies at next billing run
                {data.upcomingChargesSummary?.nextChargeDate &&
                  // Pin the displayed time to Europe/London so the cron
                  // wall-clock matches the "Next billing run · 1 Jun
                  // 2026 at 16:00 BST" banner on the upcoming-charges
                  // page regardless of the operator's browser TZ.
                  // Without `setZone` luxon renders in the browser's
                  // local TZ and a Warsaw-based admin sees "17:00 CEST"
                  // for a cron that fires at 16:00 BST.
                  ` · Next: ${DateTime.fromISO(
                    data.upcomingChargesSummary.nextChargeDate
                  )
                    .setZone("Europe/London")
                    .toFormat("dd MMM HH:mm 'BST'")}`}
              </SummarySubtext>
            </SummaryCard>

            <SummaryCard $accent="#00bc82">
              <SummaryLabel>Active subscriptions</SummaryLabel>
              <SummaryValue>{data.totalActiveSubscriptions}</SummaryValue>
              <SummarySubtext>{data.totalSubscriptions} total</SummarySubtext>
            </SummaryCard>

            <SummaryCard
              $accent={data.issueCount > 0 ? "#f9a000" : "#00bc82"}
              $clickable={data.issueCount > 0}
              $active={activeFilter === "alignment_issues"}
              onClick={() =>
                data.issueCount > 0 && handleFilterClick("alignment_issues")
              }
            >
              <SummaryLabel>Alignment issues</SummaryLabel>
              <SummaryValue>{data.issueCount}</SummaryValue>
              <SummarySubtext>
                {data.issueCount === 0
                  ? "All subscriptions aligned"
                  : activeFilter === "alignment_issues"
                  ? "Showing filtered"
                  : "Click to filter"}
              </SummarySubtext>
            </SummaryCard>

            <SummaryCard
              $accent={data.paymentFailedCount > 0 ? "#ef6355" : "#00bc82"}
              $clickable={data.paymentFailedCount > 0}
              $active={activeFilter === "payment_failed"}
              onClick={() =>
                data.paymentFailedCount > 0 &&
                handleFilterClick("payment_failed")
              }
            >
              <SummaryLabel>Payment issues</SummaryLabel>
              <SummaryValue>{data.paymentFailedCount}</SummaryValue>
              <SummarySubtext>
                {data.paymentFailedCount === 0
                  ? "No payment issues"
                  : activeFilter === "payment_failed"
                  ? "Showing filtered"
                  : "Click to filter"}
              </SummarySubtext>
            </SummaryCard>
          </SummaryGrid>
        )}

        {/* Subscriptions Table */}
        <TableContainer>
          <TableHeader>
            <TableHeaderLeft>
              <Typography variant={TypographyVariant.HEADER3}>
                Company Subscriptions
              </Typography>
              {data && (
                <>
                  <FilterPill
                    $active={activeFilter === "alignment_issues"}
                    $color="#f9a000"
                    onClick={() => handleFilterClick("alignment_issues")}
                  >
                    Alignment Issues ({data.issueCount})
                  </FilterPill>
                  <FilterPill
                    $active={activeFilter === "payment_failed"}
                    $color="#ef6355"
                    onClick={() => handleFilterClick("payment_failed")}
                  >
                    Payment Failed ({data.paymentFailedCount})
                  </FilterPill>
                  {activeFilter !== "all" && (
                    <FilterPill
                      onClick={() => setActiveFilter("all")}
                      $color="#8181a5"
                    >
                      Clear filter
                    </FilterPill>
                  )}
                </>
              )}
            </TableHeaderLeft>
            <SearchInput
              placeholder="Search company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </TableHeader>

          {loading && !data ? (
            <LoadingWrap>Loading...</LoadingWrap>
          ) : data ? (
            <ReactTableComponent
              columns={columns}
              data={data.subscriptions as any}
              onRowClick={handleRowClick as any}
              withPagination={{
                totalDocs: data.pagination.totalDocs,
                page: data.pagination.page,
                limit: data.pagination.limit,
                onPaginationChange: handlePaginationChange,
              }}
            />
          ) : (
            <LoadingWrap>Failed to load data</LoadingWrap>
          )}
        </TableContainer>
      </Container>
    </AnimateAppearanceWrapper>
  );
};

export default SubscriptionsDashboard;
